import { BaseRemoteService } from "@ofqual/portal-core/core/dataAccess";
import {
  Opts,
  Model,
  QueryParams,
  PagedResponse,
  JSONPatchDocument,
  ValidatedResponse,
} from "@ofqual/portal-core/core/types";
import { paramsToSearchString } from "@ofqual/portal-core/core/utils/urlHelper";

import { PagedResponseTo } from "@ofqual/portal-core/core/utils/pagedResponseHelpers";
import { getValidationError } from "@ofqual/portal-core/core/utils/validationHelper";
import { SocItem, fromApiModel } from "../domain/SocItem";
import { SocEvent, SocEventComplete } from "../domain/SocEvent";
import { SocMessageFilters } from "../domain/SocMessageFilters";

export class SocService extends BaseRemoteService {
  public async get(id: string, opts: Opts): Promise<SocItem> {
    const url = `${this.apiRoot}/2023/socs/${id}`;
    const response = await super.getRequest<Model>(url, opts);
    return fromApiModel(response);
  }

  public async search(
    params: QueryParams,
    opts: Opts
  ): Promise<PagedResponse<SocItem>> {
    const url = `${this.apiRoot}/2023/socs/${paramsToSearchString(params)}`;
    const response = await super.getRequest<PagedResponse<Model>>(url, opts);
    return PagedResponseTo<SocItem>(response, fromApiModel);
  }

  public async update(
    id: string,
    patchDocument: JSONPatchDocument,
    opts: Opts
  ): Promise<ValidatedResponse<SocItem>> {
    const url = `${this.apiRoot}/2023/socs/${id}`;
    try {
      const response = await this.patchRequest<SocItem>(
        url,
        patchDocument,
        opts
      );
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async send(
    id: string,
    patchDocument: JSONPatchDocument,
    shareUpn: string,
    opts: Opts
  ): Promise<ValidatedResponse<SocItem>> {
    const url = `${this.apiRoot}/2023/socs/${id}/send`;
    try {
      const response: Model = await this.patchRequest(url, {patchDocument, shareUpn}, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }
  }

  public async downloadActivityAttachment(
    id: string,
    activityId: string,
    blobId: string,
    opts: Opts
  ): Promise<Blob> {
    const url = `${this.apiRoot}/2023/socs/${id}/documents/${activityId}/download/${blobId}`;
    return await super.getRequestBlob(url, opts);
  }

  public async sendInternalMessage(
    id: string,
    files: File[],
    subject: string,
    message: string,
    recipients: string[],
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/2023/socs/${id}/internal-message`;
    const messageData = { id, subject, message, recipients };
    const messageSerialized = JSON.stringify(messageData);
    return await super.postRequestFormData(
      url,
      { messageSerialized, files },
      opts
    );
  }

  public async assign(
    id: string,
    upn: string,
    displayName: string,
    opts: Opts,
  ): Promise<SocItem> {
    const url = `${this.apiRoot}/2023/socs/${id}/assign`;
    const messageData = { id, upn, displayName };
    return await super.postRequest(url, messageData, opts);
  }

  public async unassign(
    id: string,
    opts: Opts,
  ): Promise<SocItem> {
    const url = `${this.apiRoot}/2023/socs/${id}/unassign`;
    return await super.postRequest(url, {}, opts);
  }


  public async addCaseNote(
    id: string,
    files: File[],
    note: string,
    noteType: number,
    opts: Opts
  ): Promise<unknown> {
    const url = `${this.apiRoot}/2023/socs/${id}/casenote`;
    return await super.postRequestFormData(url, { note, files, noteType }, opts);
  }

  public async close(
    id: string,
    patchDocument: JSONPatchDocument,
    opts: Opts,
  ): Promise<ValidatedResponse<SocItem>> {
    const url = `${this.apiRoot}/2023/socs/${id}/close`;
    try {
      const response: Model = await this.patchRequest<SocItem>(url, patchDocument, opts);
      return {
        isValid: true,
        data: fromApiModel(response),
      };
    } catch (err) {
      const validationError = getValidationError(err);
      if (validationError.isValidationError) {
        return { isValid: false, error: validationError.error };
      }
      throw err;
    }  
  }

  public async reopenSoc(
    id: string,
    opts: Opts,
  ): Promise<SocItem> {
    const url = `${this.apiRoot}/2023/socs/${id}/reopen`;
    return await super.postRequest(url, {}, opts);
  }

  public async sendMessage(
    id: string,
    files: File[],
    message: string,
    opts: Opts
  ) {
    const url = `${this.apiRoot}/2023/socs/${id}/message`;
    return await super.postRequestFormData(url, { message, files }, opts);
  }

  public async sendMultipleMessage(
    files: File[],
    message: string,
    filters: SocMessageFilters,
    opts: Opts
  ): Promise<number> {
    const url = `${this.apiRoot}/2023/socs/messages`;
    const filtersSerialized = JSON.stringify(filters);

    return await super.postRequestFormData(url, { message, files, filtersSerialized }, opts);
  }

  public async addEvent(id: string, event: SocEvent, opts: Opts) {
    const url = `${this.apiRoot}/2023/socs/${id}/event`;
    return await super.postRequest(url, event, opts);
  }

  public async completeEvent(id: string, eventId: string, completeEvent: SocEventComplete, opts: Opts) {
    const url = `${this.apiRoot}/2023/socs/${id}/event/${eventId}/complete`;
    return await super.postRequestFormData(
      url,
      { notes: completeEvent.notes, files: completeEvent.files },
      opts
    );
  }

  public async share(
    id: string,
    shareUpn: string,
    opts: Opts
  ): Promise<SocItem> {
    const url = `${this.apiRoot}/2023/socs/${id}/share`;
    return await this.postRequest<SocItem>(url, shareUpn, opts);
  }

  public async enableResubmit(
    id: string,
    opts: Opts,
  ): Promise<SocItem> {
    const url = `${this.apiRoot}/2023/socs/${id}/resubmit`;
    return await super.postRequest(url, {}, opts);
  }

}

